/**
 * Zustand Store Hooks
 * 
 * Re-render optimized hooks for consuming the store.
 * These wrap the bound store with useShallow to prevent unnecessary re-renders.
 * 
 * NOTE: Import these hooks from '@/stores' not from this file directly
 * to ensure proper module resolution.
 */

import { useShallow } from 'zustand/react/shallow';
import type { StoreApi, UseBoundStore } from 'zustand';

/**
 * Creates a shallow hook wrapper for any Zustand store.
 * Use this to prevent re-renders when selecting multiple properties.
 * 
 * @example
 * const useStoreShallow = createShallowHook(useBoundStore);
 * const { theme, sidebarState } = useStoreShallow((s) => ({
 *   theme: s.theme,
 *   sidebarState: s.sidebarState
 * }));
 */
export function createShallowHook<T extends object>(
  useStore: UseBoundStore<StoreApi<T>>,
) {
  return function useShallowSelector<R>(selector: (state: T) => R): R {
    return useStore(useShallow(selector));
  };
}

/**
 * Creates a value hook for selecting single primitive values.
 * For single values, this is more efficient than useShallow.
 */
export function createValueHook<T extends object>(
  useStore: UseBoundStore<StoreApi<T>>,
) {
  return function useValueSelector<R>(selector: (state: T) => R): R {
    return useStore(selector);
  };
}

/**
 * Creates an actions hook for selecting store actions.
 * Actions are stable references and don't cause re-renders.
 */
export function createActionsHook<T extends object>(
  useStore: UseBoundStore<StoreApi<T>>,
) {
  return function useActionsSelector<R extends Record<string, (...args: unknown[]) => unknown>>(
    selector: (state: T) => R,
  ): R {
    return useStore(useShallow(selector));
  };
}
